import { useRegenerateMfaRecoveryCodes } from '@hooks/useMfa';
import { getErrorMessage } from '@utils/errorMessages';
import { AlertCircle, Copy, Download, Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface MfaRecoveryCodesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MfaRecoveryCodesDialog({ open, onOpenChange }: MfaRecoveryCodesDialogProps) {
  const [codes, setCodes] = useState<string[]>([]);
  const regenerateMutation = useRegenerateMfaRecoveryCodes();

  const handleGenerate = () => {
    regenerateMutation.mutate(undefined, {
      onSuccess: data => {
        setCodes(data.recovery_codes || []);
      },
    });
  };

  const handleCopyAll = () => {
    navigator.clipboard.writeText(codes.join('\n'));
    toast.success('Recovery codes copied to clipboard');
  };

  const handleDownload = () => {
    const content = codes.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'compass-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Recovery codes downloaded');
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setCodes([]);
      regenerateMutation.reset();
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Regenerate Recovery Codes</DialogTitle>
          <DialogDescription>
            {codes.length > 0
              ? 'Save these new recovery codes in a safe place. Your previous codes are now invalid.'
              : 'This will invalidate your existing recovery codes. Make sure to save the new ones.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {regenerateMutation.isError && (
            <div className="bg-destructive/10 border border-destructive/50 text-destructive px-4 py-3 rounded-md text-sm">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="font-medium">Failed to Regenerate Codes</p>
                  <p className="mt-1">
                    {getErrorMessage(regenerateMutation.error) || 'Please try again.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {codes.length > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-2 rounded-md border bg-muted p-4">
                {codes.map((codeItem, index) => (
                  <code key={index} className="text-sm font-mono">
                    {codeItem}
                  </code>
                ))}
              </div>

              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={handleCopyAll}>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy All
                </Button>
                <Button variant="outline" className="flex-1" onClick={handleDownload}>
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-md text-sm dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-200">
                <p className="font-medium">Important</p>
                <p className="mt-1">
                  Your previous recovery codes are no longer valid. Store these new codes securely.
                </p>
              </div>

              <Button className="w-full" onClick={() => handleOpenChange(false)}>
                Done
              </Button>
            </>
          ) : (
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleGenerate} disabled={regenerateMutation.isPending}>
                {regenerateMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  'Generate New Codes'
                )}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
